import React, { useEffect, useState } from 'react';
import CocaLogo from '../../assets/coca-logo.png';

function nestErrorMessage(
  body: { message?: string | string[]; msg?: string },
  fallback: string,
): string {
  const raw = body.message ?? body.msg;
  if (Array.isArray(raw)) return raw.join('; ');
  if (typeof raw === 'string' && raw.trim()) return raw;
  return fallback;
}

interface CasdoorLoginPageProps {
  onWeChatLogin: () => void;
  oauthGateError?: string | null;
  onOauthGateErrorConsumed?: () => void;
}

/**
 * Casdoor Login Page Component
 * Displays login options with Casdoor and WeChat authentication
 * Styled to match Casdoor's official login UI
 */
export const CasdoorLoginPage: React.FC<CasdoorLoginPageProps> = ({
  onWeChatLogin,
  oauthGateError,
  onOauthGateErrorConsumed,
}) => {
  const [activeTab, setActiveTab] = useState<'verification' | 'password'>('verification');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sendCooldownSec, setSendCooldownSec] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);

  // ISO 3166-1 alpha-2 for Casdoor/libphonenumber (e.g. CN), not dial prefix (+86).
  const defaultCountryCode = import.meta.env.VITE_CASDOOR_DEFAULT_COUNTRY_CODE || 'CN';

  useEffect(() => {
    if (sendCooldownSec <= 0) return;
    const t = window.setInterval(() => {
      setSendCooldownSec((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [sendCooldownSec]);

  useEffect(() => {
    if (!oauthGateError) return;
    setError(oauthGateError);
    onOauthGateErrorConsumed?.();
  }, [oauthGateError, onOauthGateErrorConsumed]);

  const handleSendVerificationCode = async () => {
    setError('');
    const u = username.trim();
    if (!u) {
      setError('请先填写手机号或邮箱');
      return;
    }
    const isPhone = /^1\d{10}$/.test(u);
    const isEmail = u.includes('@');
    if (!isPhone && !isEmail) {
      setError('验证码登录请使用 11 位手机号或邮箱');
      return;
    }

    setSendingCode(true);
    try {
      const response = await fetch('/api/auth/casdoor/send-verification-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: u,
          ...(isPhone ? { countryCode: defaultCountryCode } : {}),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(nestErrorMessage(data, `发送失败 (${response.status})`));
      }
      setSendCooldownSec(60);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '发送验证码失败';
      setError(msg);
      console.error('[CasdoorSMS] send code failed:', err);
    } finally {
      setSendingCode(false);
    }
  };

  const handleDirectLogin = async () => {
    setError('');
    
    if (activeTab === 'password') {
      // Password login
      if (!username || !password) {
        setError('请输入账号和密码');
        return;
      }

      setLoading(true);
      try {
        const response = await fetch('/api/auth/casdoor/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username, password }),
        });

        const responseData = await response.json();

        if (!response.ok) {
          console.error('Login failed:', responseData);
          throw new Error(nestErrorMessage(responseData, `登录失败 (${response.status})`));
        }

        const { accessToken, refreshToken, user } = responseData;

        localStorage.setItem('casdoorToken', accessToken);
        localStorage.setItem('_authing_token', accessToken);
        localStorage.setItem('casdoorUser', JSON.stringify(user));
        if (refreshToken) {
          localStorage.setItem('casdoorRefreshToken', refreshToken);
        }

        window.location.reload();
      } catch (err: any) {
        console.error('Login failed:', err);
        const errorMsg = err.message || '登录失败，请重试';
        // Remove redundant "登录失败:" prefix if it exists
        setError(errorMsg.replace(/^登录失败:\s*/, ''));
      } finally {
        setLoading(false);
      }
    } else {
      const u = username.trim();
      const code = verificationCode.trim();
      if (!u || !code) {
        setError('请输入手机号/邮箱和验证码');
        return;
      }

      setLoading(true);
      try {
        const isPhone = /^1\d{10}$/.test(u);
        const response = await fetch('/api/auth/casdoor/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: u,
            code,
            ...(isPhone ? { countryCode: defaultCountryCode } : {}),
          }),
        });

        const responseData = await response.json();

        if (!response.ok) {
          console.error('Login failed:', responseData);
          throw new Error(nestErrorMessage(responseData, `登录失败 (${response.status})`));
        }

        const { accessToken, refreshToken, user } = responseData;

        localStorage.setItem('casdoorToken', accessToken);
        localStorage.setItem('_authing_token', accessToken);
        localStorage.setItem('casdoorUser', JSON.stringify(user));
        if (refreshToken) {
          localStorage.setItem('casdoorRefreshToken', refreshToken);
        }

        window.location.reload();
      } catch (err: unknown) {
        console.error('Login failed:', err);
        const errorMsg = err instanceof Error ? err.message : '登录失败，请重试';
        setError(errorMsg.replace(/^登录失败:\s*/, ''));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="flex h-screen w-screen items-center justify-center px-6 bg-[#d9d4cc]">
      <div className="w-full max-w-[440px]">
        <div className="bg-white rounded-3xl shadow-xl p-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src={CocaLogo}
              alt="日己茶同意" 
              className="w-40 h-40 object-contain"
            />
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              className={`flex-1 pb-3 text-base font-medium transition-colors relative ${
                activeTab === 'verification'
                  ? 'text-[#3b82f6]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('verification')}
            >
              验证码登录
              {activeTab === 'verification' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#3b82f6]" />
              )}
            </button>
            <button
              className={`flex-1 pb-3 text-base font-medium transition-colors relative ${
                activeTab === 'password'
                  ? 'text-[#3b82f6]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('password')}
            >
              密码登录
            </button>
          </div>

          {/* Form Content */}
          <div className="space-y-4 mb-2">
            {/* Phone Input */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="请输入手机号/账号/邮箱"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleDirectLogin()}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                         focus:outline-none focus:ring-2 focus:ring-[#3b82f6] focus:border-transparent
                         text-gray-700 placeholder-gray-400"
              />
            </div>

            {/* Verification Code / Password Input */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                type={activeTab === 'verification' ? 'text' : 'password'}
                placeholder={activeTab === 'verification' ? '请输入短信/邮件验证码' : '请输入密码'}
                value={activeTab === 'verification' ? verificationCode : password}
                onChange={(e) => activeTab === 'verification' ? setVerificationCode(e.target.value) : setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleDirectLogin()}
                className="w-full pl-12 pr-32 py-3 bg-gray-50 border border-gray-200 rounded-lg 
                         focus:outline-none focus:ring-2 focus:ring-[#3b82f6] focus:border-transparent
                         text-gray-700 placeholder-gray-400"
              />
              {activeTab === 'verification' && (
                <button
                  type="button"
                  onClick={handleSendVerificationCode}
                  disabled={sendingCode || sendCooldownSec > 0}
                  className="absolute right-2 top-1/2 z-10 -translate-y-1/2 px-4 py-1.5 text-sm
                           border border-gray-300 rounded-md hover:bg-gray-50 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingCode
                    ? '发送中…'
                    : sendCooldownSec > 0
                      ? `${sendCooldownSec}s`
                      : '发送验证码'}
                </button>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Login Button - Direct Login */}
          <button
            onClick={handleDirectLogin}
            disabled={loading}
            className="w-full py-3.5 text-base font-medium text-white bg-[#3b82f6] hover:bg-[#2563eb]
                     rounded-lg transition-all duration-200 ease-in-out 
                     shadow-sm hover:shadow-md
                     focus:outline-none focus:ring-2 focus:ring-[#3b82f6] focus:ring-offset-2 mb-6
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '登录中...' : '登 录'}
          </button>

          {/* Third Party Login */}
          <div className="text-center">
            <p className="text-sm text-gray-400 mb-4">第三方账号登录</p>
            <div className="flex justify-center">
              <button
                onClick={onWeChatLogin}
                className="w-10 h-10 rounded-full bg-[#07c160] flex items-center justify-center
                         hover:bg-[#06ad56] transition-colors shadow-sm hover:shadow-md"
                title="使用微信登录"
              >
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.045c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.03-.406-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z" />
                </svg>
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

